// Seção de manifesto/pitch nas landings, adaptada por audiência.
// Texto fonte: MANIFESTO.md no repo.

type Audiencia = 'paciente' | 'medico' | 'psicologo'

type Props = {
  audiencia: Audiencia
}

const TEXTOS: Record<Audiencia, {
  pergunta: string
  introducao: string
  desenvolvimento: string
  conclusao: string
  emoji: string
  corPrimaria: string  // hex
  corBgClass: string
  corAccentClass: string
}> = {
  paciente: {
    emoji: '👤',
    corPrimaria: '#a8e6f0',
    corBgClass: 'bg-white/8',
    corAccentClass: 'text-accent-turquesa',
    pergunta: 'Quanto tempo você perde aguardando o dia da próxima consulta chegar?',
    introducao:
      'Quantas vezes você esqueceu sintomas importantes na hora da consulta? Quantas vezes a conversa foi curta demais pra você explicar tudo que aconteceu nas últimas semanas?',
    desenvolvimento:
      'E se cada dia entre suas consultas virasse uma nota rápida — humor, sintomas, sono, o que você sentiu? Anotações simples, de 2 minutos, no seu celular. E essas notas virassem insights organizados, entregues ao seu profissional já compilados por IA?\n\nVocê não precisa mais lembrar de tudo. Só viver. Quem cuida de você chega à consulta com sua história já contada, no detalhe certo.',
    conclusao: 'Mais qualidade na análise junto a quem cuida de você — e respaldo emocional de acompanhamento real desde o primeiro dia da sua espera.',
  },
  medico: {
    emoji: '🩺',
    corPrimaria: '#6ee7b7',
    corBgClass: 'bg-white/8',
    corAccentClass: 'text-emerald-300',
    pergunta: 'Quanto tempo o seu paciente desperdiçou até chegar até você?',
    introducao:
      'Cada minuto da consulta gasto preenchendo formulários é um minuto roubado da análise clínica. E ainda assim, depende da memória do paciente conseguir reconstruir 30, 60, 90 dias num quarto de hora.',
    desenvolvimento:
      'Anamis converte o tempo de espera em anamnese estruturada. O paciente faz check-ins de 2 minutos por dia — humor, sintomas, mapa corporal de dor, sono, medicamentos. Quando você abre o paciente antes da consulta, 1 clique gera o PDF: hipóteses ranqueadas, alertas urgentes em vermelho, padrão temporal dos sintomas, CID-10 quando confiança >70%.\n\nVocê lê em 30 segundos uma anamnese mais rica do que a que o paciente conseguiria expressar em 15 minutos. Decisão clínica mais rápida e mais fundamentada.',
    conclusao: 'Big data clínico estruturado em FHIR/HL7 — pra você, pra rede de clínicas, pra planos de saúde, pra pesquisa.',
  },
  psicologo: {
    emoji: '🧠',
    corPrimaria: '#c4b5fd',
    corBgClass: 'bg-white/8',
    corAccentClass: 'text-purple-300',
    pergunta: 'Sessão de 50 minutos é uma fotografia. E o filme entre elas?',
    introducao:
      'O paciente vive entre as sessões. Sente, pensa, é gatilhado, encontra estratégias, recai. Esse material chega fragmentado — dependendo da memória dele, do que decide compartilhar, do que conseguiu nomear.',
    desenvolvimento:
      'Anamis registra continuamente: humor (1-5), ansiedade (0-10), pensamentos recorrentes, gatilhos, técnicas de coping, flag opt-in de ideação. Quando paciente sinaliza pensamento de risco, você recebe push imediato — não espera próxima sessão. Antes da sessão, IA gera relatório consolidado: padrões temporais, sinais clínicos, F-codes (CID-10 saúde mental).\n\nVocê chega à sessão com o filme da semana inteira. Continua o cuidado de onde ele realmente está, não de onde ele lembra.',
    conclusao: 'Cuidado contínuo, não pontual. Sigilo profissional preservado. CVV em destaque pra emergência. Compliance LGPD + ética CRP.',
  },
}

export default function SecaoManifesto({ audiencia }: Props) {
  const t = TEXTOS[audiencia]

  return (
    <section className="max-w-7xl mx-auto px-6 py-20 md:py-32 border-t border-white/10">
      <div className="max-w-3xl mx-auto">
        {/* Eyebrow */}
        <div className="text-center mb-8">
          <span className={`inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.25em] uppercase ${t.corAccentClass}`}>
            <span>{t.emoji}</span>
            <span>Manifesto</span>
          </span>
        </div>

        {/* Pergunta principal — destaque tipográfico */}
        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl leading-[1.1] text-balance text-center mb-10">
          {t.pergunta}
        </h2>

        {/* Texto longo */}
        <div className="space-y-6 text-base md:text-lg leading-relaxed text-white/85 text-pretty">
          <p>{t.introducao}</p>

          {t.desenvolvimento.split('\n\n').map((paragrafo, i) => (
            <p key={i}>{paragrafo}</p>
          ))}

          <p className={`font-semibold ${t.corAccentClass}`}>
            {t.conclusao}
          </p>
        </div>

        {/* Slogan central, em destaque */}
        <div className="mt-16 text-center">
          <div
            className="inline-block rounded-3xl px-8 py-6 border-2"
            style={{ borderColor: t.corPrimaria, background: `${t.corPrimaria}10` }}
          >
            <div className="text-[10px] font-bold tracking-[0.3em] uppercase mb-2 opacity-70">
              Anamis
            </div>
            <div
              className="font-display text-2xl md:text-3xl leading-tight"
              style={{ color: t.corPrimaria }}
            >
              O cuidado começa<br />antes da consulta.
            </div>
            <div className="mt-4 pt-4 border-t border-white/15 text-sm md:text-base text-white/80 italic max-w-md mx-auto leading-snug">
              Porque o tempo <strong className="not-italic" style={{ color: t.corPrimaria }}>antes da consulta</strong> — é parte da consulta.
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
